import type { SetupStepId } from "@ttc-spectree/toolbox-contract";
import { GROOMING_QUEUE_PATH } from "@/lib/grooming/paths";
import {
  FOGBUGZ_FORMULATOR_TOOL_NAME,
  FOGBUGZ_TO_ADO_PATH,
  FOGBUGZ_TO_ADO_TOOL_NAME,
  FOGBUGZ_TOOL_NAME,
  FORMULATOR_PATH,
  isFogbugzFormulatorPath,
  isFogbugzPath,
} from "@/lib/toolbox/tools";

/**
 * Plain names for the SpecTree Toolbox pages that need an Azure DevOps PAT
 * (epic PI-SPEC-SW-E288, feature PI-SPEC-SW-1466; frame 1c, 03-evaluation.md
 * section 8.6 Q5). A page that needs the PAT sends the person to Setup's
 * "Connect Azure DevOps" step with one line naming it ("Backlog needs your
 * Azure DevOps connection…"), and Setup sends them back after a pass.
 *
 * Only paths named here are returned to, so the `from` parameter cannot send
 * anyone off SpecTree (no open redirect).
 *
 * The Queue is a second kind of return target (epic PI-SPEC-SW-E290, feature
 * PI-SPEC-SW-1479, 01-roles.md section 9.6): a paused queue's "Fix it" opens
 * one Setup step (Azure DevOps, Claude Code or Git) and comes back to the
 * Queue with `?fixed=1`, which asks the app to start again. The Queue needs
 * no PAT, so it is never a PAT page (`isToolboxPatPage` drives RequireAdoPat)
 * and Setup shows no "needs your Azure DevOps connection" line for it.
 *
 * SpecTree's own My ADO Items pages (`/ado/items`, `/ado/items/:project/:id`)
 * are a third kind (PKG-PM-D Mac walk, 2026-10-23): in a browser they keep
 * SpecTree's Settings redirect, but inside SpecTree Toolbox, where the command
 * palette sends a person with no role on a link's project to them, they go to
 * Setup's "Connect Azure DevOps" step like the Toolbox pages and come back.
 */

/** The Setup page and the step a PAT redirect opens. */
export const TOOLBOX_SETUP_PATH = "/toolbox/setup";
export const ADO_SETUP_STEP = "azure-devops";

/** Project pages under /grooming/:project/… and their names. */
const GROOMING_PROJECT_PAGES: readonly (readonly [RegExp, ToolboxPageName])[] = [
  [/^\/grooming\/[^/]+\/?$/, "Backlog"],
  [/^\/grooming\/[^/]+\/backlog\/?$/, "Backlog"],
  [/^\/grooming\/[^/]+\/items\/[^/]+\/?$/, "Backlog"],
  [/^\/grooming\/[^/]+\/session(?:\/[^/]+)?\/?$/, "Session"],
  [/^\/grooming\/[^/]+\/project(?:\/[^/]+)?\/?$/, "Project"],
  // The legacy Repos URL redirects to the Project page (PKG-PM-F).
  [/^\/grooming\/[^/]+\/repos\/?$/, "Project"],
  [/^\/grooming\/[^/]+\/one\/?$/, "One item"],
];

/** Every name a page can have; Setup shows only these. */
export const TOOLBOX_PAGE_NAMES = [
  "Grooming",
  "Backlog",
  "Session",
  "Project",
  "One item",
  "ADO Formulator",
  FOGBUGZ_TO_ADO_TOOL_NAME,
  "My ADO Items",
] as const;
export type ToolboxPageName = (typeof TOOLBOX_PAGE_NAMES)[number];

/**
 * The plain name of a PAT-guarded Toolbox page, or null when `pathname` is
 * not one (SpecTree's own ADO pages, the Setup page, anything else).
 */
export function toolboxPageName(pathname: string): ToolboxPageName | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (path === FORMULATOR_PATH) return "ADO Formulator";
  // FogBugz to ADO (epic PI-SPEC-SW-E311) creates the Bug with the PM's own PAT.
  if (path === FOGBUGZ_TO_ADO_PATH) return FOGBUGZ_TO_ADO_TOOL_NAME;
  if (path === "/grooming") return "Grooming";
  if (path === "/grooming/one") return "One item";
  // Project-less pages that need no PAT never redirect here.
  if (path === "/grooming/queue" || /^\/grooming\/[^/]+\/agenda\//.test(path)) return null;
  for (const [pattern, name] of GROOMING_PROJECT_PAGES) {
    if (pattern.test(path)) return name;
  }
  return null;
}

/**
 * The plain name of a Toolbox page that needs the person's saved FogBugz key
 * rather than an ADO PAT: the FogBugz event log summary (epic
 * PI-SPEC-SW-E307) or the FogBugz Formulator (epic PI-SPEC-SW-E309), else
 * null. Never a PAT page, so Setup's "Connect Azure DevOps" step never names
 * or returns to it; without a key its guard links to Settings instead.
 */
export function fogbugzPageName(
  pathname: string,
): typeof FOGBUGZ_TOOL_NAME | typeof FOGBUGZ_FORMULATOR_TOOL_NAME | null {
  if (isFogbugzFormulatorPath(pathname)) return FOGBUGZ_FORMULATOR_TOOL_NAME;
  return isFogbugzPath(pathname) ? FOGBUGZ_TOOL_NAME : null;
}

/** "My ADO Items" for SpecTree's own ADO pages (the list and an item), else null. */
export function adoItemsPageName(pathname: string): "My ADO Items" | null {
  return /^\/ado\/items(?:\/[^/]+\/[^/]+)?\/?$/.test(pathname) ? "My ADO Items" : null;
}

/** The plain name a PAT redirect gives `pathname`: a Toolbox page, or My ADO Items. */
export function patPageName(pathname: string): ToolboxPageName | null {
  return toolboxPageName(pathname) ?? adoItemsPageName(pathname);
}

/**
 * The Setup steps a queue "Fix it" may open (the steps whose fix is on Setup).
 * `project-code` since epic PI-SPEC-SW-E318: a GitHub repository's sign-in is
 * "Sign in to GitHub" on that step.
 */
export const FIX_IT_SETUP_STEPS = ["azure-devops", "claude-code", "git", "project-code"] as const satisfies readonly SetupStepId[];
export type FixItSetupStep = (typeof FIX_IT_SETUP_STEPS)[number];

/** True for a Setup step a queue "Fix it" may open. */
export function isFixItSetupStep(step: string | null | undefined): step is FixItSetupStep {
  return typeof step === "string" && (FIX_IT_SETUP_STEPS as readonly string[]).includes(step);
}

/** The query parameter that tells the Queue it is back from a Fix it on Setup ("start again"). */
export const QUEUE_FIXED_PARAM = "fixed";

/** Where Setup sends the person after a queue Fix it step passes. */
export const QUEUE_FIX_RETURN_PATH = `${GROOMING_QUEUE_PATH}?${QUEUE_FIXED_PARAM}=1`;

/** The Queue's plain name, for "Back to Queue". */
export const QUEUE_PAGE_NAME = "Queue";

/** True when `pathname` is the Queue (a Fix it return target, never a PAT page). */
export function isQueueReturnPath(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return path === GROOMING_QUEUE_PATH;
}

/** True when `pathname` is a Toolbox page (grooming routes and the Toolbox Formulator) that sends to Setup for the PAT. */
export function isToolboxPatPage(pathname: string): boolean {
  return toolboxPageName(pathname) !== null;
}

/**
 * `from` when it is safe to return to: a single-slash path on this site that
 * names a PAT-guarded Toolbox page, My ADO Items or the Queue (a Fix it return). Anything
 * else (`//evil.example`, a full URL, `/settings`, `/toolbox/setup`) gives null.
 */
export function safeToolboxReturnPath(from: string | null | undefined): string | null {
  if (!from || !from.startsWith("/") || from.startsWith("//") || from.includes("\\")) return null;
  const pathname = from.split(/[?#]/, 1)[0] ?? "";
  return patPageName(pathname) !== null || isQueueReturnPath(pathname) ? from : null;
}

/** Where a PAT-guarded Toolbox page (or, in the app, My ADO Items) sends someone without a PAT. */
export function adoSetupRedirect(pathname: string, search = ""): string {
  const name = patPageName(pathname) ?? "This page";
  const params = new URLSearchParams({ step: ADO_SETUP_STEP, from: `${pathname}${search}`, page: name });
  return `${TOOLBOX_SETUP_PATH}?${params.toString()}`;
}

/**
 * Where a paused queue's "Fix it" opens Setup: the step, a return to the
 * Queue with `?fixed=1`, and the Queue's name. Without a step, Setup opens at
 * the top (a setup pause whose code this version does not know).
 */
export function queueFixSetupPath(step: FixItSetupStep | null): string {
  const params = new URLSearchParams({
    ...(step ? { step } : {}),
    from: QUEUE_FIX_RETURN_PATH,
    page: QUEUE_PAGE_NAME,
  });
  return `${TOOLBOX_SETUP_PATH}?${params.toString()}`;
}

/**
 * Setup opened at one step, with no return (a Fix it on a stopped run, a
 * failed item or One item, where the person tries again by hand). Without a
 * step, Setup opens at the top.
 */
export function setupStepPath(step: FixItSetupStep | null): string {
  return step ? `${TOOLBOX_SETUP_PATH}?${new URLSearchParams({ step }).toString()}` : TOOLBOX_SETUP_PATH;
}
